import React, { useEffect, useState } from "react";
import { getCsrfToken } from "../../../../public/api/authApi";
import {
  getCallPurposes,
  createCallPurpose,
  updateCallPurpose,
  deleteCallPurpose,
} from "../../api/callLogsApi.js";
import GlobalFormShell from "../../../../global/forms/GlobalFormShell";
import {
  GlobalTextField,
  GlobalSelectField,
  GlobalButton,
} from "../../../../global/forms/GlobalFormFields";
import GlobalBadge from "../../../../global/GlobalBadge";
import { GlobalTableActionButton } from "../../../../global/GlobalTableActionButton";
import GlobalSuccessFlashModal from "../../../../global/GlobalSuccessFlashModal";
import { useCallLogRole } from "../../utils/useCallLogRole";
import { PHONE_CALL_LOGS_CONTROL_SUBMODULE_SLUG } from "../../utils/callLogPermissionSlugs";

const STATUS_OPTIONS = [
  { value: 1, label: "Active" },
  { value: 2, label: "Inactive" },
];

const CallPurposeMaster = ({ onDirectoryChange }) => {
  const perms = useCallLogRole(PHONE_CALL_LOGS_CONTROL_SUBMODULE_SLUG);
  const canManage = !perms.permissionsLoading && perms.canManageMasters;
  const canDelete = !perms.permissionsLoading && perms.canDeleteMasters;
  const [purposes, setPurposes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  const [formData, setFormData] = useState({ purpose_name: "", status_id: 1 });

  const clearFieldError = (name) => {
    setFieldErrors((prev) => ({ ...prev, [name]: null }));
  };

  const fetchPurposes = async () => {
    setLoading(true);
    try {
      const resp = await getCallPurposes();
      const rows = Array.isArray(resp?.data) ? resp.data : [];
      setPurposes(rows.filter((r) => Number(r.is_active) === 1));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPurposes();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!formData.purpose_name.trim()) {
      newErrors.purpose_name = "Purpose name is required";
    }
    if (Object.keys(newErrors).length > 0) {
      setFieldErrors(newErrors);
      setError("Please fix the fields below.");
      return;
    }
    setFieldErrors({});

    setLoading(true);
    try {
      let csrfToken = localStorage.getItem("csrf-token");
      if (!csrfToken) {
        csrfToken = await getCsrfToken();
        localStorage.setItem("csrf-token", csrfToken);
      }

      if (editingId) {
        await updateCallPurpose(editingId, formData, csrfToken);
        setSuccessMsg("Call purpose updated successfully.");
      } else {
        await createCallPurpose(formData, csrfToken);
        setSuccessMsg("Call purpose added successfully.");
      }

      setFormData({ purpose_name: "", status_id: 1 });
      setEditingId(null);
      setError(null);
      await fetchPurposes();
      if (typeof onDirectoryChange === "function") {
        await onDirectoryChange();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (purpose) => {
    setEditingId(purpose.id);
    setFormData({
      purpose_name: purpose.purpose_name,
      status_id:
        purpose.status_id != null && purpose.status_id !== ""
          ? Number(purpose.status_id)
          : 1,
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this purpose?")) return;

    setLoading(true);
    try {
      let csrfToken = localStorage.getItem("csrf-token");
      if (!csrfToken) {
        csrfToken = await getCsrfToken();
        localStorage.setItem("csrf-token", csrfToken);
      }

      await deleteCallPurpose(id, csrfToken);
      setSuccessMsg("Call purpose deleted successfully.");
      await fetchPurposes();
      if (typeof onDirectoryChange === "function") {
        await onDirectoryChange();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setFormData({ purpose_name: "", status_id: 1 });
    setError(null);
    setFieldErrors({});
  };

  return (
    <div className="occl-master-section">
      <h3 className="ub-section__subhead" style={{ marginTop: 0 }}>Call Purpose Master</h3>
      <GlobalSuccessFlashModal
        message={successMsg}
        onDismiss={() => setSuccessMsg("")}
      />

      <form onSubmit={handleSubmit} className="occl-master-form" noValidate>
        {canManage ? (
        <GlobalFormShell
          legend={editingId ? "Edit call purpose" : "Add call purpose"}
          showAlert={!!error}
          alertText={error}
          actions={
            <>
              <GlobalButton type="submit" variant="primary" size="sm" disabled={loading}>
                {editingId ? "Update" : "Add"} Purpose
              </GlobalButton>
              {editingId && (
                <GlobalButton variant="ghost" size="sm" onClick={handleCancel}>
                  Cancel
                </GlobalButton>
              )}
            </>
          }
        >
          <GlobalTextField
            label="Purpose Name *"
            value={formData.purpose_name}
            onChange={(v) => { setFormData((prev) => ({ ...prev, purpose_name: v })); clearFieldError("purpose_name"); }}
            placeholder="Enter call purpose (e.g., Sales Inquiry, Support)"
            error={fieldErrors.purpose_name}
          />
          {editingId ? (
            <GlobalSelectField
              label="Status"
              value={String(formData.status_id)}
              onChange={(v) =>
                setFormData((prev) => ({ ...prev, status_id: Number(v) }))
              }
              options={STATUS_OPTIONS}
              placeholderOption="Select status"
            />
          ) : null}
        </GlobalFormShell>
        ) : (
          <p style={{ color: "#64748b", fontSize: "0.875rem", marginBottom: "1rem" }}>
            You have view-only access to call purposes.
          </p>
        )}
      </form>

      <div className="ub-table-wrap occl-master-list">
        {loading && purposes.length === 0 ? (
          <p style={{ color: "#64748b", fontSize: "0.875rem" }}>Loading...</p>
        ) : purposes.length === 0 ? (
          <p style={{ color: "#64748b", fontSize: "0.875rem" }}>
            No purposes found. Add one above.
          </p>
        ) : (
          <table className="ub-table">
            <thead>
              <tr>
                <th>Purpose Name</th>
                <th>Status</th>
                <th className="ub-table__col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {purposes.map((purpose) => (
                <tr key={purpose.id}>
                  <td>{purpose.purpose_name}</td>
                  <td>
                    <GlobalBadge
                      active={Number(purpose.status_id ?? purpose.is_active) === 1}
                    />
                  </td>
                  <td className="ub-table__col-actions">
                    {canManage ? (
                    <div className="ub-table-actions">
                      <GlobalTableActionButton action="edit" onClick={() => handleEdit(purpose)} />
                      {canDelete ? (
                      <GlobalTableActionButton
                        action="delete"
                        variant="ghost"
                        onClick={() => handleDelete(purpose.id)}
                      />
                      ) : null}
                    </div>
                    ) : (
                      <span style={{ color: "#94a3b8" }}>—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default CallPurposeMaster;
